// GroundTruth-specific pure data transforms used by the Sidekick CLW
// summary pane. Input is always a `ctsReview` payload from
// /api/upload/review-cts — output shapes feed Sidekick's shared review
// grid and summary tiles. No React or context coupling: a future
// per-account submodule under Sidekick's CampaignLaunchWorkflow can
// mirror this module's shape.

import {
  GT_ISSUE_FIELD_MAP,
  gtAudienceLabel,
  isGtAudiencePlaceholder,
  GT_LOCATION_GROUP_PLACEHOLDER,
} from './gtConstants';
import { displayFinding } from './gtInstructionLabels';
import { gtPlannedUnits } from './gtPlannedUnits';

// Issue.adgroup_name strings from the validator occasionally carry
// trailing whitespace or differ from the parser's ad_group_name by
// punctuation spacing ("Gen X , Millennials" vs "Gen X, Millennials").
// Normalising both sides for the row lookup means cells still light
// up + bullets stay clickable even when the validator output drifts.
export function normalizeAdgroupName(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')   // collapse runs of whitespace
    .replace(/\s*([,_])\s*/g, '$1') // strip whitespace around commas / underscores
    .trim();
}

// Split a compound validator field_name like "start_date/end_date" or
// "url,click_thru_url" into the individual column keys it references.
// Cross-field rules in gt_semantic_validator.py emit these compounds —
// see ~10 call sites grepping field_name= with `/`. Returns mapped keys
// (GT_ISSUE_FIELD_MAP) when possible, falls back to the raw segment.
export function splitGtIssueFields(explicit) {
  return String(explicit || '')
    .split(/[\/,]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => GT_ISSUE_FIELD_MAP[s] || s);
}

export function resolveGtIssueField(issue) {
  const explicit = issue?.field_name || issue?.field;
  if (explicit) {
    // Compound field refs: jump to the FIRST mapped column. The full
    // expansion happens in buildGtReviewRows so every mentioned cell
    // still lights up.
    const parts = splitGtIssueFields(explicit);
    if (parts.length > 0) return parts[0];
  }
  // Category fallback for issues without an explicit field name.
  const cat = String(issue?.category || '').toLowerCase();
  if (cat.includes('product')) return 'product';
  if (cat.includes('audience')) return 'targeting_product';
  if (cat.includes('creative')) return 'creative_sizes';
  if (cat.includes('budget')) return 'budget';
  if (cat.includes('bid')) return 'bid_price';
  if (cat.includes('location filter')) return 'location_filter';
  if (cat.includes('location lists')) return 'location_filter';
  if (cat.includes('location')) return 'location_targeting';
  if (cat.includes('device')) return 'device_types';
  if (cat.includes('flight') || cat.includes('date')) return 'start_date';
  return 'name';
}

// A CTS envelope that carries no plan at all.
//
// The GT handlers write a RESULT-LESS envelope whenever the IO never became a
// campaign_config: an Excel parse failure, or a PDF whose Stage-2 conversion
// failed. gt_api_handler.py writes `{ success: false, errors: [...], io_json,
// conversion_metadata }` with NO `result` key at all — so every consumer that
// renders a review has to tell that apart from "nothing has run yet", or a
// failed run reads as a still-loading one. (It did: a PDF whose conversion
// timed out after 24 minutes rendered as "No CTS review data available yet.")
//
// Engine internals — model names, per-model timeout lists, retry counts —
// are for logs, not for the operator's error card (UAT 0817: "too much
// detail"). The full string still reaches the console/network tab.
const _INTERNALS = /claude|gpt|gemini|sonnet|opus|openai|anthropic|\bmodels?\b|timeout|timed out/i;

/**
 * A failure string fit for the card. Parentheticals that leak internals are
 * stripped; if internals remain, the message collapses to its own
 * "<something> failed" headline, or to null when there is nothing safe left.
 */
const _displayableFailure = (text) => {
  if (!text) return null;
  let out = String(text).replace(/\s*\(([^()]*)\)/g, (m, inner) => (_INTERNALS.test(inner) ? '' : m));
  if (_INTERNALS.test(out)) {
    const headline = /^([^:]{0,80}failed)\s*:/i.exec(out);
    return headline ? `${headline[1]}.` : null;
  }
  return out.trim() || null;
};

/** Does `outer` already say everything `inner` says? (wrapper-vs-reason dedupe) */
const _saysTheSame = (outer, inner) => {
  const norm = (v) => String(v || '').trim().toLowerCase().replace(/[\s.]+$/, '');
  const a = norm(outer);
  const b = norm(inner);
  return Boolean(b) && (a === b || a.includes(b));
};

// Returns null for a usable review, or the failure detail to render.
export function gtCtsFailure(ctsReview) {
  if (!ctsReview || typeof ctsReview !== 'object') return null;
  // A parsed plan wins regardless of the flag: a review can carry blocking
  // validation errors AND a full config, and that is a reviewable plan.
  if (ctsReview.result?.adgroups?.length) return null;
  const errors = Array.isArray(ctsReview.errors) ? ctsReview.errors.filter(Boolean) : [];
  const convError = ctsReview.conversion_metadata?.error || null;
  const message = errors[0] || convError || null;
  // `success === false` is the explicit signal. A result-less envelope that
  // carries an error string counts too — the Excel path predates the flag.
  if (ctsReview.success !== false && !message) return null;
  const displayMessage = _displayableFailure(message);
  // The converter's own reason, when it ADDS anything to the headline the
  // handler wrapped it in ("PDF conversion failed: <convError>" would print
  // the same sentence twice) AND survives the internals filter.
  const displayDetail = _displayableFailure(
    convError && !_saysTheSame(message, convError) ? convError : null,
  );
  return {
    message: displayMessage || 'The IO could not be converted into a campaign plan.',
    detail: displayDetail && !_saysTheSame(displayMessage, displayDetail) ? displayDetail : null,
    errors,
    // Stage 1 (PDF → IO JSON) can succeed while Stage 2 fails. Saying so tells
    // the operator the upload itself was readable, so a retry is worth trying
    // before they go hunting for a bad PDF.
    ioParsed: Boolean(ctsReview.io_json),
  };
}

// One row per adgroup. Validation issues from ctsReview.issues[] decorate
// matching rows via row.annotations (keyed by field, value { type, msg }).
// Accepts both the new JSON shape (`adgroup_name` / `field_name`) and
// the older (`ad_group_name` / `field`) — the validator has emitted
// both across releases.
export function buildGtReviewRows(ctsReview) {
  if (!ctsReview?.result?.adgroups) return [];
  const adgroups = ctsReview.result.adgroups || [];
  const plannedUnits = gtPlannedUnits(ctsReview.result);
  // Keyed by normalised adgroup name so trailing-space / punctuation
  // drift between the validator's adgroup_name and the parser's
  // ad_group_name doesn't drop annotations on the floor.
  const issuesByAdgroup = new Map();
  for (const issue of (ctsReview.issues || [])) {
    const target = issue.adgroup_name || issue.ad_group_name || issue.adGroupName || issue.target;
    if (!target) continue;
    const key = normalizeAdgroupName(target);
    const list = issuesByAdgroup.get(key) || [];
    list.push(issue);
    issuesByAdgroup.set(key, list);
  }
  // Severity rank used to decide which annotation wins when several
  // issues collide on the same cell. Errors (m) > warnings (q) > infos (i).
  const sevRank = { m: 2, q: 1, i: 0 };
  return adgroups.map((ag, idx) => {
    const issues = issuesByAdgroup.get(normalizeAdgroupName(ag.ad_group_name)) || [];
    const annotations = {};
    for (const it of issues) {
      const sev = String(it.severity || 'warning').toLowerCase();
      const type = sev === 'error' ? 'm' : sev === 'info' ? 'i' : 'q';
      // Expand compound field refs ("start_date/end_date") so every
      // referenced column lights up — not just the first one. Falls back
      // to the category-based resolver when field_name is empty.
      const explicit = it.field_name || it.field;
      const fields = explicit ? splitGtIssueFields(explicit) : [resolveGtIssueField(it)];
      for (const field of fields) {
        const prior = annotations[field];
        // Same cell can have multiple issues. Keep the most severe one
        // visible, but stash the rest in `extra` so hover / detail panel
        // can show them too.
        if (!prior || sevRank[type] > sevRank[prior.type]) {
          annotations[field] = {
            type,
            msg: displayFinding(it.message || it.msg) || 'Validation issue',
            category: it.category,
            extra: prior ? [prior, ...(prior.extra || [])] : (annotations[field]?.extra || []),
          };
        } else {
          prior.extra = [...(prior.extra || []), { type, msg: displayFinding(it.message), category: it.category }];
        }
      }
    }
    return {
      rowId: `gt-ag-${idx}`,
      // The platform ad-group id, published when the launch result carried it
      // (gtLauncherToCtsReview maps std_assignment_result.assignments[].adgroup_id
      // into adgroup_id). Post-launch edits target it; a positional gt-ag-<i>
      // rowId alone can't reach the GT API.
      adgroup_id: ag.adgroup_id || null,
      idx: idx + 1,
      name: ag.ad_group_name || '—',
      channel: 'GT',
      fmt: ag.product ? String(ag.product).slice(0, 4).toUpperCase() : 'MOB',
      // GT-specific fields (used by GT_GROUP_COLS)
      product: ag.product || '',
      package: ag.package_number != null ? String(ag.package_number) : '',
      // The NAME of the package, which is what gt_campaign_launcher passes to
      // create_ad_package() — the grid only ever surfaced package_number, so the
      // field that actually names the package on the platform was uneditable.
      package_name: ag.package_name || '',
      device_types: ag.device_types || '',
      device_targeting: ag.device_targeting || '',
      drive_to_type: ag.drive_to_type || '',
      drive_to_name: ag.drive_to_type_name || '',
      location_targeting: ag.location_targeting || '',
      location_filter: ag.location_filter || '',
      // Location Groups cell — single-select object {id, name}; the display
      // label must match what the picker stages for remove/replace matching.
      // Placeholder Location Group (92006) is the pipeline's no-pick default, not a
      // user choice — UAT 0817: don't display it as a selected value. (The
      // config keeps it; the launcher's fallback behaviour is unchanged.)
      location_group: (ag.location_group?.id && ag.location_group?.name
          && String(ag.location_group.id) !== GT_LOCATION_GROUP_PLACEHOLDER.id)
        ? `${ag.location_group.name} · ${ag.location_group.id}`
        : '—',
      neighborhood_type: ag.neighborhood_type || '',
      // On Premise only (in_store | on_lot | retail_block); required there (R6).
      proximity_mode: ag.proximity_mode || '',
      radial: ag.radial != null ? String(ag.radial) : '',
      targeting_product: ag.targeting_product || '',
      targeting_identifier: ag.targeting_identifier || '',
      optimization_strategy: ag.optimization_strategy || '',
      demographic_targeting: ag.demographic_targeting || '',
      creative_sizes: ag.creative_sizes || '',
      creative_type: ag.creative_type || '',
      // UAT 0817: editable text, defaulting to "v1" when the converter left
      // it empty.
      creative_versions: ag.creative_versions_running ?? 'v1',
      placement_320x50: ag.placement_id_320x50 || '',
      placement_300x250: ag.placement_id_300x250 || '',
      placement_320x480: ag.placement_id_320x480 || '',
      // The fourth placement size; identity row key (no legacy short alias).
      placement_id_728x90: ag.placement_id_728x90 || '',
      bid_price: ag.bid_price != null ? Number(ag.bid_price) : null,
      impression_clicks: plannedUnits[idx],
      bid_type: ag.bid_type || '',
      billability_type: ag.billability_type || '',
      tracking_visits_to: ag.tracking_visits_to || '',
      special_notes: ag.special_notes || '',
      // Merged Audiences cell. The grid cell shows a count; the detail panel
      // expands audienceList. On Premise rows render no audiences (R14) even
      // if a legacy config carried one.
      // The 49124 "Audience Targeting Placeholder" is the pipeline's no-pick
      // seed, not a user choice — UAT 0817: don't display it as a selected
      // audience. (The config keeps it; R12 retires it when a real pick is
      // saved, and the launcher treats it as no-pick.)
      audiences: ((() => {
        const entries = (Array.isArray(ag.audiences) ? ag.audiences.filter(Boolean) : [])
          .filter((e) => !isGtAudiencePlaceholder(e));
        const product = String(ag.product || '').trim().toLowerCase();
        if (product.includes('on premise')) return '—';
        return entries.length ? `${entries.length} audience${entries.length === 1 ? '' : 's'}` : '—';
      }))(),
      audienceList: ((() => {
        const entries = (Array.isArray(ag.audiences) ? ag.audiences.filter(Boolean) : [])
          .filter((e) => !isGtAudiencePlaceholder(e));
        const product = String(ag.product || '').trim().toLowerCase();
        if (product.includes('on premise')) return '—';
        return entries.length ? entries.map(gtAudienceLabel).join(' · ') : '—';
      }))(),
      // The same labels as an ARRAY. audienceList joins entries with ' · ' and
      // each label itself contains ' · ' (name · id · type), so the joined
      // string cannot be split back into entries — membership checks (the bulk
      // editor's remove/replace preview) need the unambiguous form.
      audienceEntries: ((() => {
        const entries = (Array.isArray(ag.audiences) ? ag.audiences.filter(Boolean) : [])
          .filter((e) => !isGtAudiencePlaceholder(e));
        const product = String(ag.product || '').trim().toLowerCase();
        if (product.includes('on premise')) return [];
        return entries.map(gtAudienceLabel);
      }))(),
      start_date: ag.start_date || null,
      end_date: ag.end_date || null,
      // Aliases for compatibility with the shared review grid header cells
      market: ag.location_targeting || '—',
      tactic: ag.targeting_product || '—',
      start: ag.start_date || null,
      end: ag.end_date || null,
      budget: ag.budget != null ? Number(ag.budget) : null,
      cpm: ag.bid_price != null ? Number(ag.bid_price) : null,
      imps: null,
      annotations,
    };
  });
}

// Mirrors the shape of summarizeLauncherResult so summary tiles can be
// rendered without branching at the call site (the parent component
// picks between this and summarizeLauncherResult based on destination).
export function summarizeReviewCtsResult(ctsReview) {
  if (!ctsReview) return null;
  const r = ctsReview.result || {};
  const campaign = r.campaign || {};
  const sum = r.summary || {};
  const errors = Array.isArray(ctsReview.errors) ? ctsReview.errors : [];
  const warnings = Array.isArray(ctsReview.warnings) ? ctsReview.warnings : [];
  const infos = Array.isArray(ctsReview.infos) ? ctsReview.infos : [];
  return {
    platform: 'GT',
    ioCount: 1,
    adGroupCount: sum.adgroups_items_found ?? (r.adgroups?.length || 0),
    packageCount: 0,
    creativeCount: sum.creatives_new_items_found ?? (r.creative_new?.length || 0),
    audienceCount: (sum.custom_audiences_items_found ?? 0) + (sum.third_party_audiences_items_found ?? 0),
    locationCount: sum.location_lists_items_found ?? (r.location_lists?.length || 0),
    tagCount: sum.dcm_tags_items_found ?? (r.dcm_tags?.length || 0),
    totalImpressions: null,
    totalBudget: campaign.io_budget != null ? Number(campaign.io_budget) : null,
    avgCpm: null,
    flightStart: campaign.start_date || null,
    flightEnd: campaign.end_date || null,
    campaignName: campaign.salesForce_opportunity_name || campaign.io_name || null,
    ioName: campaign.io_name || null,
    kpi1: campaign.kpi_1 || null,
    kpi2: campaign.kpi_2 || null,
    advertiserId: r.account_id || null,
    tenantId: r.tenant_id || null,
    issueCount: errors.length + warnings.length + infos.length,
    errorCount: errors.length,
    warningCount: warnings.length,
    infoCount: infos.length,
  };
}
